import { getAxiosErrorMessage } from "apps/visits/core/utils/axios-error";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  createValidateUserDTO,
  useValidateUser,
  type ValidateUserDTO,
  type ValidateUserErrorResponse,
  type ValidateUserResponse,
} from "../api/validate-user";
import type { FormValues } from "../components/user-id-form";
import { getConfig } from "../models/voucher-distribution";
import { useUserState } from "../store/use-user-state";
import { useVouchersDistributionState } from "../store/use-vouchers-distribution-state";
import type { ConfigName } from "../types/config-name";

interface UseValidateUserOptions {
  configName: ConfigName;
  redirectRoute: string;
  onSuccess?: (data: ValidateUserResponse, variables: ValidateUserDTO) => void;
  onError?: (error: AxiosError<ValidateUserErrorResponse>, variables: ValidateUserDTO) => void;
}

export const useValidateUserHandler = ({ configName, redirectRoute, onSuccess, onError }: UseValidateUserOptions) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { setUser } = useUserState();
  const { voucherDistribution } = useVouchersDistributionState();
  const config = getConfig(voucherDistribution, configName);

  const { mutate, isLoading } = useValidateUser({
    config: {
      onSuccess: (data, variables) => {
        const { externalUid, token } = data;
        const email = "email" in variables ? variables.email : undefined;

        setUser({ externalUid, email, isValid: true, error: undefined, token });

        if (onSuccess) {
          onSuccess(data, variables);
        }

        history.push(redirectRoute);
      },
      onError: (error, variables) => {
        const externalUid = "externalUid" in variables ? variables.externalUid : undefined;
        const email = "email" in variables ? variables.email : undefined;

        setUser({
          externalUid,
          email,
          isValid: false,
          error: t(`VOUCHERS_DISTRIBUTION.ERRORS.${getAxiosErrorMessage(error)?.toUpperCase()}`),
        });

        if (onError) {
          onError(error, variables);
        }
      },
    },
  });

  const handleSubmitUseIdForm = ({ userId }: FormValues) => {
    setUser({ externalUid: "", isValid: false, error: undefined });

    if (!voucherDistribution) {
      return;
    }

    const authorizationMethod = config?.authorizationMethod || "external_uid_authorization";

    try {
      const mutationData = createValidateUserDTO(
        authorizationMethod,
        {
          slug: voucherDistribution.slug,
          configName,
        },
        {
          externalUid: authorizationMethod === "external_uid_authorization" ? userId.trim() : undefined,
          email: authorizationMethod === "email_authorization" ? userId.trim() : undefined,
        },
      );
      mutate(mutationData);
    } catch (error) {
      console.error("Error creating validate user DTO:", error);
      setUser({
        externalUid: "",
        isValid: false,
        error: t("VOUCHERS_DISTRIBUTION.ERRORS.INVALID_AUTHORIZATION_METHOD"),
      });
    }
  };

  return { handleSubmitUseIdForm, isLoading };
};
